import { useMemo } from 'react';
import { forwardRef } from '@heroui/system';
import { usePhoneInput, parseCountry } from 'react-international-phone';
import { Input } from '@heroui/react';
import { sortBy } from 'lodash-es';
import cn from 'classnames';
import { PHONE_INPUT_DEFAULT_PLACEHOLDER, DEFAULT_COUNTRY, DEFAULT_COUNTRIES } from './constants';
import { buildCountriesData, getExamplePlaceholder, getFlag } from './utils';
import type { CountryItem, PhoneInputProps, PhoneInputClassNames } from './types';
import { CountryAutocomplete } from './CountryAutocomplete';

export type { PhoneInputProps, PhoneInputClassNames };

const PhoneInput = forwardRef<'div', PhoneInputProps>((props, ref) => {
  const {
    value,
    countries = DEFAULT_COUNTRIES,
    defaultCountry = DEFAULT_COUNTRY,
    disabled,
    isInvalid = false,
    onChange,
    onBlur,
    placeholder,
    selectLabel,
    classNames,
    label,
    error,
    selectAriaLabel,
    inputAriaLabel,
  } = props;

  const countriesData = useMemo(() => buildCountriesData(countries), [countries]);

  const { country, inputRef, inputValue, handlePhoneValueChange, setCountry } = usePhoneInput({
    defaultCountry: defaultCountry.toLowerCase(),
    value,
    countries: countriesData,
    disableDialCodeAndPrefix: true,
    onChange: (data) => {
      onChange(data.inputValue ? data.phone : '');
    },
  });

  const countryItems: CountryItem[] = useMemo(
    () =>
      sortBy(
        countriesData.map((c) => {
          const { iso2, dialCode, name } = parseCountry(c);
          return {
            iso2,
            flag: getFlag(iso2),
            dialCode,
            name,
          };
        }),
        'name',
      ),
    [countriesData],
  );

  const currentPlaceholder = placeholder || getExamplePlaceholder(country.iso2) || PHONE_INPUT_DEFAULT_PLACEHOLDER;

  return (
    <div ref={ref} className="flex gap-2 place-items-start">
      <CountryAutocomplete
        countryItems={countryItems}
        currentIso2={country.iso2}
        setCountry={setCountry}
        label={selectLabel}
        hasLabel={!!label}
        isInvalid={isInvalid}
        isDisabled={disabled}
        ariaLabel={selectAriaLabel}
        className={classNames?.select}
        triggerClassName={classNames?.selectTrigger}
      />
      <Input
        ref={inputRef}
        value={inputValue}
        isDisabled={disabled}
        isInvalid={isInvalid}
        onChange={handlePhoneValueChange}
        onBlur={onBlur}
        placeholder={currentPlaceholder}
        aria-label={inputAriaLabel}
        errorMessage={isInvalid ? error : undefined}
        label={label}
        classNames={{
          base: cn('tmdx-input group', classNames?.input),
          inputWrapper: [
            'bg-base-60',
            'group-data-[focus=true]:bg-base-40',
            'group-data-[invalid=true]:bg-base-60!',
            'group-data-[invalid=true]:group-data-[hover=true]:bg-base-60!',
            'tmdx-input__wrapper',
            'rounded-lg',
            'transition-none!',
            !label && 'min-h-8 h-8',
            !!label && 'hasLabel',
            'shadow-none',
          ],
          input: [
            'text-base-5',
            'placeholder:text-base-30',
            'text-m-r!',
            'group-data-[invalid=true]:text-base-5!',
            'tmdx-input__item',
          ],
          label: [
            'text-base-20',
            'text-s-r!',
            'tmdx-input__label',
            'group-focus-within:text-base-10',
            'group-data-[invalid=true]:text-danger!',
          ],
          description: ['text-base-20', 'text-s-r', 'group-data-[invalid=true]:text-danger', 'form-item__description'],
          innerWrapper: ['bg-transparent'],
          clearButton: ['opacity-100!'],
        }}
        type="tel"
      />
    </div>
  );
});

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
